import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectionStrategy,
  TemplateRef,
} from "@angular/core";
import { trigger, style, animate, transition } from "@angular/animations";
import { formatLabel } from "@swimlane/ngx-charts";

export enum D0Types {
  positive = "positive",
  negative = "negative",
}

@Component({
  selector: "g[iris-charts-box-plot-series]",
  templateUrl: "./iris-chart-box-plot-series.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("animationState", [
      transition(":leave", [
        style({
          opacity: 1,
        }),
        animate(500, style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class IrisBoxPlotSeriesComponent implements OnChanges {
  @Input() dims;
  @Input() series;
  @Input() xScale;
  @Input() yScale;
  @Input() colors;
  @Input() gradient: boolean;
  @Input() activeEntries: any[];
  @Input() seriesName: string;
  @Input() tooltipDisabled: boolean = false;
  @Input() tooltipTemplate: TemplateRef<any>;
  @Input() roundEdges: boolean;
  @Input() animations: boolean = true;
  @Input() showDataLabel: boolean = false;
  @Input() dataLabelFormatting: any;

  @Output() select = new EventEmitter();
  @Output() activate = new EventEmitter();
  @Output() deactivate = new EventEmitter();
  @Output() dataLabelHeightChanged = new EventEmitter();

  tooltipPlacement: string;
  tooltipType: string;

  bars: any;
  x: any;
  y: any;
  barsForDataLabels: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    total: number;
    series: string;
  }> = [];

  ngOnChanges(changes): void {
    this.update();
  }

  update(): void {
    this.updateTooltipSettings();
    let width;
    if (this.series.length) {
      width = this.xScale.bandwidth();
    }

    this.bars = this.series.map((d, index) => {
      const label = d.name;
      const formattedLabel = formatLabel(label);
      const roundEdges = this.roundEdges;

      const validValues = d.values.filter((x) => x || x === 0);
      const count = validValues.length;
      const min = count === 0 ? 0 : Math.min(...validValues);
      const max = count === 0 ? 0 : Math.max(...validValues);
      const median = count === 0 ? 0 : this.quantile(validValues, 0.5);
      const lowerQuartile = count === 0 ? 0 : this.quantile(validValues, 0.25);
      const upperQuartile = count === 0 ? 0 : this.quantile(validValues, 0.75);
      const average = count === 0 ? 0 : this.average(validValues);

      const bar: any = {
        min,
        max,
        average,
        median,
        lowerQuartile,
        upperQuartile,
        label,
        roundEdges,
        data: d,
        width,
        formattedLabel,
        height: 0,
        x: 0,
        y: 0,
        yWhiskers: 0,
        heightWhiskers: 0,
      };

      bar.x = this.xScale(label);
      bar.y = this.yScale(upperQuartile);
      bar.height = Math.abs(
        this.yScale(upperQuartile) - this.yScale(lowerQuartile)
      );
      bar.yWhiskers = this.yScale(max);
      bar.heightWhiskers = Math.abs(this.yScale(max) - this.yScale(min));
      bar.yMedian = this.yScale(median);
      bar.yAverage = this.yScale(average);

      if (this.colors.scaleType === "ordinal") {
        bar.color = this.colors.getColor(label);
      } else {
        // bar.color = this.colors.getColor(value);
        // bar.gradientStops = this.colors.getLinearGradientStops(value);
      }

      let tooltipLabel = formattedLabel;
      if (this.seriesName) {
        tooltipLabel = `${this.seriesName} • ${formattedLabel}`;
        bar.data.series = this.seriesName;
      }

      bar.tooltipText = this.tooltipDisabled
        ? undefined
        : `
          <span class="tooltip-label">${tooltipLabel}</span>
          <span class="tooltip-val">Minimum: ${min.toLocaleString()}</span>
          <span class="tooltip-val">Maximum: ${max.toLocaleString()}</span>
          <span class="tooltip-val">Mittelwert: ${average.toLocaleString()}</span>
          <span class="tooltip-val">Unteres Quartil: ${lowerQuartile.toLocaleString()}</span>
          <span class="tooltip-val">Median: ${median.toLocaleString()}</span>
          <span class="tooltip-val">Oberes Quartil: ${upperQuartile.toLocaleString()}</span>
          <span class="tooltip-val">Abgegebene Stimmen: ${
            validValues.length
          }</span>
          <span class="tooltip-val">Enthaltungen: ${
            d.values.length - validValues.length
          }</span>
        `;

      return bar;
    });

    this.updateDataLabels();
  }

  quantile(values, p) {
    values = this.sort(values);
    var pos = Math.max(0, (values.length + 1) * p - 1);
    var base = Math.floor(pos);
    var rest = pos - base;
    if (values[base + 1] !== undefined) {
      return values[base] + rest * (values[base + 1] - values[base]);
    } else {
      return values[base];
    }
  }

  sort(values) {
    return values.sort(function (x, y) {
      return x - y;
    });
  }

  sum(values) {
    return values.reduce(function (x, y) {
      return x + y;
    }, 0);
  }

  average(values) {
    return this.sum(values) / values.length;
  }

  updateDataLabels() {
    this.barsForDataLabels = this.series.map((d) => {
      const section: any = {};
      section.series = this.seriesName ? this.seriesName : d.name;
      section.total = d.value;
      section.x = this.xScale(d.name);
      section.y = this.yScale(0);
      section.height = this.yScale(section.total) - this.yScale(0);
      section.width = this.xScale.bandwidth();
      return section;
    });
  }

  updateTooltipSettings() {
    this.tooltipPlacement = this.tooltipDisabled ? undefined : "top";
    this.tooltipType = this.tooltipDisabled ? undefined : "tooltip";
  }

  isActive(entry): boolean {
    if (!this.activeEntries) return false;
    const item = this.activeEntries.find((d) => {
      return entry.name === d.name && entry.series === d.series;
    });
    return item !== undefined;
  }

  onClick(data): void {
    this.select.emit(data);
  }

  trackBy(index, bar): string {
    return bar.label;
  }

  trackDataLabelBy(index, barLabel) {
    return index + "#" + barLabel.series + "#" + barLabel.total;
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/iris-charts-box-plot-chart/iris-chart-box-plot-series.component.ts